import { Fragment, useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime, formatQty } from '../api';
import { useAuth } from '../auth';
import { Card, Empty, Loading, Notice, ReasonBadge } from '../components/ui';
import { FotoGalerie } from '../components/Foto';
import type { Movement, MovementReason } from '../types';

const REASONS: { value: MovementReason | ''; label: string }[] = [
  { value: '', label: 'alle Buchungsarten' },
  { value: 'wareneingang', label: 'Wareneingang' },
  { value: 'warenausgang', label: 'Warenausgang' },
  { value: 'umlagerung', label: 'Umlagerung' },
  { value: 'korrektur', label: 'Korrektur' },
  { value: 'inventur', label: 'Inventur' },
  { value: 'retoure', label: 'Retoure' },
];

export function MovementsPage() {
  const { ownerId } = useAuth();
  const [movements, setMovements] = useState<Movement[] | null>(null);
  const [reason, setReason] = useState<MovementReason | ''>('');
  const [limit, setLimit] = useState(100);
  const [error, setError] = useState('');
  const [offenFoto, setOffenFoto] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setMovements(
        await api<Movement[]>('/api/stock/movements', {
          query: { ownerId, reason: reason || undefined, limit },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }, [ownerId, reason, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Notice kind="info">
        Das Bewegungsjournal ist unveränderlich. Fehlbuchungen werden nicht gelöscht, sondern durch
        eine Korrekturbuchung ausgeglichen - das ist Voraussetzung für die GoBD-Nachvollziehbarkeit.
      </Notice>

      <div className="toolbar">
        <label className="field">
          <span>Buchungsart</span>
          <select value={reason} onChange={(e) => setReason(e.target.value as MovementReason | '')}>
            {REASONS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Anzahl</span>
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            {[50, 100, 250, 500].map((value) => (
              <option key={value} value={value}>
                letzte {value}
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        <button onClick={() => void load()}>Aktualisieren</button>
      </div>

      <Card padded={false}>
        {!movements ? (
          <Loading />
        ) : movements.length === 0 ? (
          <Empty>Keine Bewegungen gefunden.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Art</th>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th className="num">Menge</th>
                  <th>Von</th>
                  <th>Nach</th>
                  <th>Charge</th>
                  <th>Beleg</th>
                  <th>Benutzer</th>
                  <th>Foto</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <Fragment key={movement.id}>
                    <tr title={movement.note ?? undefined}>
                      <td className="small muted">{formatDateTime(movement.booked_at)}</td>
                      <td>
                        <ReasonBadge reason={movement.reason} />
                      </td>
                      <td>
                        <span className="mono">{movement.sku}</span> {movement.article_name}
                      </td>
                      <td className="small">{movement.owner_name}</td>
                      <td className="num">{formatQty(movement.qty, movement.unit)}</td>
                      <td className="mono small">{movement.from_code ?? '—'}</td>
                      <td className="mono small">{movement.to_code ?? '—'}</td>
                      <td className="small">{movement.lot_code ?? '—'}</td>
                      <td className="small mono">{movement.document_ref ?? '—'}</td>
                      <td className="small muted">{movement.user_name ?? '—'}</td>
                      <td>
                        {movement.reason === 'wareneingang' ? (
                          <button
                            className="linklike"
                            onClick={() => setOffenFoto((id) => (id === movement.id ? null : movement.id))}
                          >
                            {offenFoto === movement.id ? 'schließen' : 'Fotos'}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                    {offenFoto === movement.id && (
                      <tr>
                        <td colSpan={11} style={{ background: '#f7f9fb' }}>
                          <FotoGalerie refType="wareneingang" refId={movement.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
